/**
 * A-01 承認待ち。自分が上長を務める承認グループのスタッフの提出だけを表示する。
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../../api/endpoints.js';
import { Card, EmptyState, ErrorMessage, Kpi, Loading, Notice } from '../../components/ui.js';
import { formatDateLabel, formatDateTime, formatDuration } from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

type Filter = 'all' | 'resubmitted' | 'warning';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'すべて' },
  { value: 'resubmitted', label: '再提出' },
  { value: 'warning', label: '警告あり' },
];

export function ApprovalsPage(): React.JSX.Element {
  const [filter, setFilter] = useState<Filter>('all');
  const state = useAsync(() => endpoints.approvals(filter), [filter]);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data) return <Loading />;

  const { scope, totals, items } = state.data;

  return (
    <>
      <Card title="承認範囲" description="所属部門ではなく、承認グループで承認範囲が決まります。">
        <div className="grid gap-3 sm:grid-cols-3">
          <Kpi label="承認グループ" value={scope.groupName ?? '未設定'} />
          <Kpi label="上長" value={scope.approverName} />
          <Kpi label="対象スタッフ" value={`${scope.staffCount}名`} />
        </div>
        <Notice tone="info">
          <span>承認ルート：スタッフが提出 → グループ上長が承認 → 管理部が月次処理</span>
        </Notice>
      </Card>

      {scope.groupId === null && (
        <Notice tone="warn" title="承認グループの上長に設定されていません">
          管理部に承認グループの設定を依頼してください。
        </Notice>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Kpi label="承認待ち" value={`${totals.pending}件`} />
        <Kpi label="うち警告あり" value={`${totals.withWarning}件`} />
        <Kpi label="今月の承認済み" value={`${totals.approvedThisMonth}件`} />
      </div>

      <Card
        title="承認待ちの日報"
        action={
          <div className="flex gap-1">
            {FILTERS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`btn btn--sm ${filter === item.value ? 'btn--primary' : 'btn--secondary'}`}
                onClick={() => setFilter(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        }
      >
        {items.length === 0 ? (
          <EmptyState message="承認待ちの日報はありません。" />
        ) : (
          <div className="table-wrap">
            <table className="table table--hover">
              <thead>
                <tr>
                  <th>スタッフ</th>
                  <th>所属部門</th>
                  <th>対象日</th>
                  <th>提出日時</th>
                  <th className="text-right">稼働時間</th>
                  <th>確認事項</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.report.id}>
                    <td className="font-semibold">{item.employee.name}</td>
                    <td className="text-ink-muted">{item.employee.department}</td>
                    <td className="whitespace-nowrap">
                      {formatDateLabel(item.report.date)}
                      {item.resubmitted && <span className="ml-1 pill pill--warn">再提出</span>}
                    </td>
                    <td className="whitespace-nowrap">{formatDateTime(item.report.submittedAt)}</td>
                    <td className="text-right">
                      {formatDuration(item.computed?.totalMinutes ?? 0)}
                    </td>
                    <td>
                      {item.issues.length === 0 ? (
                        <span className="text-ink-muted">—</span>
                      ) : (
                        <span className="pill pill--warn">{item.issues.length}件</span>
                      )}
                    </td>
                    <td>
                      <Link
                        className="btn btn--primary btn--sm"
                        to={`/approvals/${item.report.id}`}
                      >
                        確認する
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
